// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Common/Layout';
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import Consumers from './components/Consumers/Consumers';
import ApiKeys from './components/ApiKeys/ApiKeys';
import Services from './components/Services/Services';
import Tenants from './components/Tenants/Tenants';
import './App.css';

interface PrivateRouteProps {
  children: React.ReactElement;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { token, loading } = useAuth();

  if (loading) return <div className="loading">Loading...</div>;

  return token ? children : <Navigate to="/login" />;
};

interface RoleRouteProps {
  children: React.ReactElement;
  allowedRoles: string[];
}

const RoleRoute: React.FC<RoleRouteProps> = ({ children, allowedRoles }) => {
  const { user } = useAuth();

  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

const AppRoutes: React.FC = () => {
  const { token } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={token ? <Navigate to="/" /> : <Login />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </PrivateRoute>
        }
      />
      {/* SystemAdmin only */}
      <Route
        path="/tenants"
        element={
          <PrivateRoute>
            <RoleRoute allowedRoles={['SystemAdmin']}>
              <Layout>
                <Tenants />
              </Layout>
            </RoleRoute>
          </PrivateRoute>
        }
      />
      {/* Tenant users only */}
      <Route
        path="/consumers"
        element={
          <PrivateRoute>
            <RoleRoute allowedRoles={['TenantAdmin', 'TenantUser']}>
              <Layout>
                <Consumers />
              </Layout>
            </RoleRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/api-keys"
        element={
          <PrivateRoute>
            <RoleRoute allowedRoles={['TenantAdmin', 'TenantUser']}>
              <Layout>
                <ApiKeys />
              </Layout>
            </RoleRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/services"
        element={
          <PrivateRoute>
            <RoleRoute allowedRoles={['TenantAdmin', 'TenantUser']}>
              <Layout>
                <Services />
              </Layout>
            </RoleRoute>
          </PrivateRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
